import { useState, useEffect, Suspense } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import {
  EventHeader,
  EventDetails,
  ClientInfo,
  UnifiedPaymentSection,
  VendorPaymentsSection,
  DetailedItemAllocations,
  ClientPaymentModal,
  AddServiceModal,
  AddVendorModal,
  EventActivityTimeline,
  GiveVendorPaymentModal,
  VendorList,
} from "../components";
import { EventDetail, Task } from "../components/types";
import { eventsApi, clientPaymentsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { Loader } from "lucide-react";

function EventDetailPageContent() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === "staff";

  const eventId = typeof window !== "undefined" 
    ? new URLSearchParams(window.location.search).get("id")
    : null;

  const [event, setEvent] = useState<EventDetail | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [customerPayments, setCustomerPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isClientPaymentModalOpen, setIsClientPaymentModalOpen] = useState(false);
  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);
  const [isAddVendorModalOpen, setIsAddVendorModalOpen] = useState(false);
  const [isVendorPaymentModalOpen, setIsVendorPaymentModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);

  useEffect(() => {
    if (!eventId) {
      setError("No event ID provided");
      setLoading(false);
      return;
    }

    const loadEventDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await eventsApi.get(parseInt(eventId));
        const eventData = response.data?.data || response.data;
        
        if (!eventData || !eventData.id) {
          setError("Event not found");
          setEvent(null);
          return;
        }

        if (eventData.id !== parseInt(eventId)) {
          setError("Event ID mismatch");
          setEvent(null);
          return;
        }

        setEvent(eventData);

        if (eventData?.id) {
          try {
            const tasksResponse = await eventsApi.getTasks(eventData.id);
            if (tasksResponse) {
              setTasks(tasksResponse.data?.data || tasksResponse.data || []);
            }
          } catch (err) {
            console.log("Could not load tasks");
          }

          try {
            const logsResponse = await eventsApi.getLogs?.(eventData.id);
            if (logsResponse) {
              setLogs(logsResponse.data?.data || logsResponse.data || []);
            }
          } catch (err) {
            console.log("Could not load activity logs");
            setLogs([]);
          }

          try {
            const paymentsResponse = await clientPaymentsApi.list({ event_id: eventData.id });
            if (paymentsResponse) {
              setCustomerPayments(paymentsResponse.data?.data || paymentsResponse.data || []);
            }
          } catch (err) {
            console.log("Could not load customer payments");
            if (eventData.clientPayments) {
              setCustomerPayments(eventData.clientPayments);
            }
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load event details";
        setError(message);
        showToast(message, "error");
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };

    loadEventDetails();
  }, [eventId, showToast]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader className="w-8 h-8 animate-spin text-stone-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !event) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-600 font-medium mb-4">{error || "Event not found"}</p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const handleVendorPaymentFromSection = (vendor: any) => {
    setSelectedVendor({
      vendor_id: vendor.vendor_id,
      vendor_name: vendor.vendor_name,
      total_amount: vendor.total_amount,
      paid_amount: vendor.paid_amount,
      balance: vendor.balance,
      items: vendor.items,
      payments: vendor.payments,
    });
    setIsVendorPaymentModalOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <EventHeader event={event} />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {event.financials && (
              <DetailedItemAllocations
                services={event.financials.services || []}
                vendors={event.financials.vendors || []}
                onAddService={() => setIsAddServiceModalOpen(true)}
                onAddVendor={() => setIsAddVendorModalOpen(true)}
                onSwitchVendor={(item, type) => {
                  showToast(`Switch vendor for ${type}: ${item.name}`, "info");
                }}
                onDeleteItem={(item, type) => {
                  showToast(`Delete ${type}: ${item.name}`, "info");
                }}
              />
            )}
            
            <div className="mt-6 border-t border-stone-200 pt-6">
              <VendorPaymentsSection
                eventId={event.id}
                vendors={event.financials?.vendors || []}
                onPayClick={() => {
                  showToast("Select a vendor to make a payment", "info");
                }}
                onPayVendor={handleVendorPaymentFromSection}
              />
            </div>
            
            {event.financials && (
              <div className="mt-6 border-t border-stone-200 pt-6">
                <UnifiedPaymentSection
                  customer={event.financials.customer}
                  payments={customerPayments.length > 0 ? customerPayments : event.clientPayments}
                  onAddPayment={() => setIsClientPaymentModalOpen(true)}
                  event={{
                    id: event.id,
                    name: event.name,
                    start_date: event.start_date,
                    venue: event.venue,
                    client: event.client,
                  }}
                />
              </div>
            )}
          </div>

          <div className="space-y-6">
            <EventDetails
              event={event}
              tasksCount={tasks.length}
              invoicesCount={event.invoices?.length || 0}
            />
            
            {event.client && <ClientInfo client={event.client} />}
            
            <EventActivityTimeline logs={logs} />

            <VendorList
              vendors={event.eventVendors || []}
              onAddVendor={() => setIsAddVendorModalOpen(true)}
              onAddService={() => setIsAddServiceModalOpen(true)}
            />
          </div>
        </div>
      </div>

      {isClientPaymentModalOpen && (
        <ClientPaymentModal
          isOpen={isClientPaymentModalOpen}
          onClose={() => setIsClientPaymentModalOpen(false)}
          eventId={event!.id}
          customer={event!.financials?.customer}
          payments={event!.clientPayments || []}
          onPaymentAdded={() => {
            setIsClientPaymentModalOpen(false);
            window.location.reload();
          }}
          event={{
            id: event!.id,
            name: event!.name,
            start_date: event!.start_date,
            venue: event!.venue,
            client: event!.client,
          }}
        />
      )}

      {isAddServiceModalOpen && (
        <AddServiceModal
          isOpen={isAddServiceModalOpen}
          onClose={() => setIsAddServiceModalOpen(false)}
          eventId={event!.id}
          onServiceAdded={() => {
            setIsAddServiceModalOpen(false);
            window.location.reload();
          }}
        />
      )}

      {isAddVendorModalOpen && (
        <AddVendorModal
          isOpen={isAddVendorModalOpen}
          onClose={() => setIsAddVendorModalOpen(false)}
          eventId={event!.id}
          onVendorAdded={() => {
            setIsAddVendorModalOpen(false);
            window.location.reload();
          }}
        />
      )}

      {isVendorPaymentModalOpen && selectedVendor && (
        <GiveVendorPaymentModal
          isOpen={isVendorPaymentModalOpen}
          onClose={() => {
            setIsVendorPaymentModalOpen(false);
            setSelectedVendor(null);
          }}
          eventId={event!.id}
          vendor={selectedVendor}
          onPaymentAdded={() => {
            setIsVendorPaymentModalOpen(false);
            setSelectedVendor(null);
            window.location.reload();
          }}
        />
      )}
    </DashboardLayout>
  );
}

function LoadingFallback() {
  return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    </DashboardLayout>
  );
}

function EventDetailPageWrapper() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <EventDetailPageContent />
    </Suspense>
  );
}

export default function EventDetailPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <EventDetailPageWrapper />
    </Suspense>
  );
}
